import React, { useState, useEffect } from "react";
import InstructorSidebar from "../common/instructorSidebar";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import {
  getPromoCodes,
  createPromoCode,
  updatePromoCode,
  togglePromoCodeStatus,
  deletePromoCode,
  getPromoCodeUsages,
  PromoCode,
  PromoCodeUsage,
} from "../../../api/promoCode";
import { rupiahFormatter } from "../../../utils/helpers";
import { useNotify } from "../../../provider/NotificationProvider";

interface CustomPaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

const CustomPagination: React.FC<CustomPaginationProps> = ({
  current,
  total,
  pageSize,
  onChange,
  onPageSizeChange,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (current >= totalPages - 3) {
        pages.push(1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "...", current - 1, current, current + 1, "...", totalPages);
      }
    }
    return pages;
  };

  const pages = getPageNumbers();

  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 w-100">
      <div className="text-muted small fw-medium" style={{ color: "#718096" }}>
        Page {current} of {totalPages}
      </div>

      <div className="d-flex align-items-center gap-2">
        {/* Prev Button */}
        <button
          type="button"
          disabled={current <= 1}
          onClick={() => onChange(current - 1)}
          className="btn btn-sm d-flex align-items-center justify-content-center border-0 p-0 shadow-none"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            backgroundColor: "#f1f5f9",
            color: "#475569",
            opacity: current <= 1 ? 0.4 : 1,
            cursor: current <= 1 ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <i className="isax isax-arrow-left-2 fs-14" />
        </button>

        {/* Page buttons */}
        {pages.map((p, idx) => {
          if (typeof p === "string") {
            return (
              <span key={idx} className="px-1 text-muted small">
                ...
              </span>
            );
          }
          const isActive = p === current;
          return (
            <button
              key={idx}
              type="button"
              onClick={() => onChange(p)}
              className="btn btn-sm d-flex align-items-center justify-content-center border-0 p-0 fw-semibold shadow-none"
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "50%",
                backgroundColor: isActive ? "#6366f1" : "#f8fafc",
                color: isActive ? "#ffffff" : "#334155",
                fontSize: "14px",
                boxShadow: isActive ? "0 2px 10px rgba(99, 102, 241, 0.4)" : "none",
                transition: "all 0.2s ease",
              }}
            >
              {p}
            </button>
          );
        })}

        {/* Next Button */}
        <button
          type="button"
          disabled={current >= totalPages}
          onClick={() => onChange(current + 1)}
          className="btn btn-sm d-flex align-items-center justify-content-center border-0 p-0 shadow-none"
          style={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            backgroundColor: "#f1f5f9",
            color: "#475569",
            opacity: current >= totalPages ? 0.4 : 1,
            cursor: current >= totalPages ? "not-allowed" : "pointer",
            transition: "all 0.2s ease",
          }}
        >
          <i className="isax isax-arrow-right-3 fs-14" />
        </button>

        {/* Page size dropdown */}
        {onPageSizeChange && (
          <div className="ms-2">
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="form-select form-select-sm text-secondary fw-semibold shadow-none"
              style={{
                borderRadius: "8px",
                borderColor: "#e2e8f0",
                fontSize: "13px",
                paddingRight: "28px",
                cursor: "pointer",
                height: "36px",
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminPromoCode = () => {
  const notify = useNotify();
  // State Promo Codes (Tab 1)
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loadingCodes, setLoadingCodes] = useState<boolean>(true);
  const [pageCodes, setPageCodes] = useState<number>(1);
  const [limitCodes, setLimitCodes] = useState<number>(10);
  const [totalCodes, setTotalCodes] = useState<number>(0);
  const [searchCodes, setSearchCodes] = useState<string>("");

  // State Usages (Tab 2)
  const [usages, setUsages] = useState<PromoCodeUsage[]>([]);
  const [loadingUsages, setLoadingUsages] = useState<boolean>(true);
  const [pageUsages, setPageUsages] = useState<number>(1);
  const [limitUsages, setLimitUsages] = useState<number>(10);
  const [totalUsages, setTotalUsages] = useState<number>(0);
  const [searchUsages, setSearchUsages] = useState<string>("");

  // Modal State
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingCode, setEditingCode] = useState<PromoCode | null>(null);

  // Form Fields
  const [codeStr, setCodeStr] = useState<string>("");
  const [discLtType, setDiscLtType] = useState<"PERCENTAGE" | "NOMINAL" | "">("PERCENTAGE");
  const [discLtVal, setDiscLtVal] = useState<string>("");
  const [discCourseType, setDiscCourseType] = useState<"PERCENTAGE" | "NOMINAL" | "">("PERCENTAGE");
  const [discCourseVal, setDiscCourseVal] = useState<string>("");
  const [discCbtType, setDiscCbtType] = useState<"PERCENTAGE" | "NOMINAL" | "">("PERCENTAGE");
  const [discCbtVal, setDiscCbtVal] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [maxUses, setMaxUses] = useState<string>("");
  const [expiresAt, setExpiresAt] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  // Fetch Promo Codes
  const fetchCodes = async () => {
    setLoadingCodes(true);
    try {
      const res = await getPromoCodes({ page: pageCodes, limit: limitCodes, search: searchCodes });
      if (res?.data) {
        setPromoCodes(res.data.data || []);
        setTotalCodes(res.data.total || 0);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingCodes(false);
    }
  };

  // Fetch Usages
  const fetchUsages = async () => {
    setLoadingUsages(true);
    try {
      const res = await getPromoCodeUsages({ page: pageUsages, limit: limitUsages, search: searchUsages });
      if (res?.data) {
        setUsages(res.data.data || []);
        setTotalUsages(res.data.total || 0);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingUsages(false);
    }
  };

  useEffect(() => {
    fetchCodes();
  }, [pageCodes, limitCodes, searchCodes]);

  useEffect(() => {
    fetchUsages();
  }, [pageUsages, limitUsages, searchUsages]);

  // Download CSV Handler
  const handleDownloadCSV = () => {
    if (!usages || usages.length === 0) {
      notify.error({ message: "Peringatan", description: "Tidak ada data penggunaan untuk diunduh." });
      return;
    }

    const headers = [
      "Order ID",
      "Tanggal",
      "Nama Produk",
      "Tipe Produk",
      "Kode Promo",
      "Harga Sebelum Apply",
      "Diskon",
      "Harga Setelah Apply",
      "Nama User",
      "Email User",
    ];

    const rows = usages.map((u) => {
      const dateStr = new Date(u.created_at).toLocaleDateString("id-ID");
      const title = `"${(u.course?.title || "").replace(/"/g, '""')}"`;
      const type = u.course?.type || "";
      const code = u.promo_code_str || u.promo_code?.code || "";
      const priceBefore = Number(u.total || 0) + Number(u.discount_amount || 0);
      const discount = Number(u.discount_amount || 0);
      const total = Number(u.total || 0);
      const userName = `"${(u.fullname || u.user?.name || "").replace(/"/g, '""')}"`;
      const email = u.email || "";

      return [
        u.order_id,
        dateStr,
        title,
        type,
        code,
        priceBefore,
        discount,
        total,
        userName,
        email,
      ].join(",");
    });

    const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(","), ...rows].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `monitoring_kode_promo_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notify.success({ message: "Sukses", description: "Data CSV berhasil diunduh." });
  };

  // Open Modal Create / Edit
  const handleOpenModal = (item?: PromoCode) => {
    if (item) {
      setEditingCode(item);
      setCodeStr(item.code);
      setDiscLtType(item.discount_lt_type || "");
      setDiscLtVal(item.discount_lt_value ? String(item.discount_lt_value) : "");
      setDiscCourseType(item.discount_course_type || "");
      setDiscCourseVal(item.discount_course_value ? String(item.discount_course_value) : "");
      setDiscCbtType(item.discount_cbt_type || "");
      setDiscCbtVal(item.discount_cbt_value ? String(item.discount_cbt_value) : "");
      setIsActive(item.is_active);
      setMaxUses(item.max_uses ? String(item.max_uses) : "");
      setExpiresAt(item.expires_at ? item.expires_at.substring(0, 16) : "");
    } else {
      setEditingCode(null);
      setCodeStr("");
      setDiscLtType("PERCENTAGE");
      setDiscLtVal("");
      setDiscCourseType("PERCENTAGE");
      setDiscCourseVal("");
      setDiscCbtType("PERCENTAGE");
      setDiscCbtVal("");
      setIsActive(true);
      setMaxUses("");
      setExpiresAt("");
    }
    setShowModal(true);
  };

  // Save Form Handler
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!codeStr.trim()) {
      notify.error({ message: "Error", description: "Kode Promo tidak boleh kosong" });
      return;
    }

    setSaving(true);
    try {
      const payload: Partial<PromoCode> = {
        code: codeStr.toUpperCase().trim(),
        discount_lt_type: discLtType ? (discLtType as any) : null,
        discount_lt_value: discLtVal ? parseFloat(discLtVal) : null,
        discount_course_type: discCourseType ? (discCourseType as any) : null,
        discount_course_value: discCourseVal ? parseFloat(discCourseVal) : null,
        discount_cbt_type: discCbtType ? (discCbtType as any) : null,
        discount_cbt_value: discCbtVal ? parseFloat(discCbtVal) : null,
        is_active: isActive,
        max_uses: maxUses ? parseInt(maxUses) : null,
        expires_at: expiresAt ? expiresAt : null,
      };

      if (editingCode) {
        await updatePromoCode(editingCode.id, payload);
        notify.success({ message: "Sukses", description: "Kode Promo berhasil diperbarui" });
      } else {
        await createPromoCode(payload);
        notify.success({ message: "Sukses", description: "Kode Promo berhasil dibuat" });
      }

      setShowModal(false);
      fetchCodes();
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.errors?.code?.[0] || "Gagal menyimpan kode promo";
      notify.error({ message: "Error", description: msg });
    } finally {
      setSaving(false);
    }
  };

  // Toggle Status Handler
  const handleToggle = async (item: PromoCode) => {
    try {
      await togglePromoCodeStatus(item.id);
      notify.success({ message: "Sukses", description: `Status kode ${item.code} telah diperbarui` });
      fetchCodes();
    } catch (err: any) {
      notify.error({ message: "Error", description: "Gagal mengubah status kode promo" });
    }
  };

  // Delete Handler
  const handleDelete = async (id: number) => {
    if (window.confirm("Apakah Anda yakin ingin menghapus Kode Promo ini?")) {
      try {
        await deletePromoCode(id);
        notify.success({ message: "Terhapus", description: "Kode promo berhasil dihapus." });
        fetchCodes();
      } catch (err: any) {
        notify.error({ message: "Error", description: "Gagal menghapus kode promo" });
      }
    }
  };

  // Render Discount Summary Badge
  const renderDiscountSummary = (item: PromoCode) => {
    const parts = [];
    if (item.discount_lt_type && item.discount_lt_value) {
      parts.push(
        `- ${item.discount_lt_type === "PERCENTAGE" ? `${item.discount_lt_value}%` : rupiahFormatter(item.discount_lt_value)} LT`
      );
    }
    if (item.discount_course_type && item.discount_course_value) {
      parts.push(
        `- ${item.discount_course_type === "PERCENTAGE" ? `${item.discount_course_value}%` : rupiahFormatter(item.discount_course_value)} Course`
      );
    }
    if (item.discount_cbt_type && item.discount_cbt_value) {
      parts.push(
        `- ${item.discount_cbt_type === "PERCENTAGE" ? `${item.discount_cbt_value}%` : rupiahFormatter(item.discount_cbt_value)} CBT`
      );
    }

    if (parts.length === 0) {
      return <span className="text-muted">Tidak ada diskon</span>;
    }

    return (
      <div className="d-flex flex-column gap-1">
        {parts.map((p, idx) => (
          <span key={idx} className="badge bg-light text-dark text-wrap text-start border">
            {p}
          </span>
        ))}
      </div>
    );
  };

  return (
    <>
      <Breadcrumb title="Kelola Kode Promo" />

      <div className="page-content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />

            <div className="col-lg-9">
              <div className="card shadow-sm border-0 rounded-3">
                <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <h5 className="mb-0 fw-bold text-dark">Kelola Kode Promo</h5>

                  <button
                    className="btn btn-primary fw-medium d-inline-flex align-items-center gap-1"
                    onClick={() => handleOpenModal()}
                    style={{ borderRadius: "8px" }}
                  >
                    <i className="isax isax-add-circle" /> Buat Kode Promo
                  </button>
                </div>

                <div className="card-body p-4">
                  {/* DAFTAR KODE PROMO */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold text-secondary text-uppercase">Daftar Kode Promo</h6>
                    <div style={{ width: "250px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Cari kode promo..."
                        value={searchCodes}
                        onChange={(e) => {
                          setSearchCodes(e.target.value);
                          setPageCodes(1);
                        }}
                      />
                    </div>
                  </div>

                  <div className="table-responsive mb-4">
                    <table className="table table-hover align-middle border-top">
                      <thead className="table-light">
                        <tr>
                          <th>Kode</th>
                          <th>Diskon</th>
                          <th className="text-center">Jumlah Penggunaan</th>
                          <th className="text-center">Status</th>
                          <th className="text-end">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingCodes ? (
                          <tr>
                            <td colSpan={5} className="text-center py-4">
                              <div className="spinner-border text-primary spinner-border-sm me-2" role="status" />
                              Memuat data...
                            </td>
                          </tr>
                        ) : promoCodes.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center py-4 text-muted">
                              Belum ada Kode Promo yang dibuat.
                            </td>
                          </tr>
                        ) : (
                          promoCodes.map((item) => (
                            <tr key={item.id}>
                              <td>
                                <span className="fw-bold text-danger fs-15">{item.code}</span>
                                {item.expires_at && (
                                  <div className="small text-muted">
                                    Exp: {new Date(item.expires_at).toLocaleDateString("id-ID")}
                                  </div>
                                )}
                              </td>
                              <td>{renderDiscountSummary(item)}</td>
                              <td className="text-center">
                                <span className="badge bg-secondary rounded-pill px-3 py-2 fs-13">
                                  {item.uses_count ?? 0}
                                </span>
                              </td>
                              <td className="text-center">
                                <div className="form-check form-switch d-inline-block">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={item.is_active}
                                    onChange={() => handleToggle(item)}
                                    style={{ cursor: "pointer" }}
                                  />
                                </div>
                              </td>
                              <td className="text-end">
                                <button
                                  className="btn btn-sm btn-outline-info me-2 rounded-circle"
                                  title="Edit"
                                  onClick={() => handleOpenModal(item)}
                                >
                                  <i className="isax isax-edit" />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger rounded-circle"
                                  title="Hapus"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  <i className="isax isax-trash" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {totalCodes > 0 && (
                    <div className="mt-3">
                      <CustomPagination
                        current={pageCodes}
                        total={totalCodes}
                        pageSize={limitCodes}
                        onChange={(p) => setPageCodes(p)}
                        onPageSizeChange={(sz) => {
                          setLimitCodes(sz);
                          setPageCodes(1);
                        }}
                      />
                    </div>
                  )}

                  <hr className="my-5" />

                  {/* RIWAYAT PENGGUNAAN KODE PROMO */}
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <h6 className="mb-0 fw-bold text-secondary text-uppercase">Monitoring Penggunaan Kode Promo</h6>
                    <div className="d-flex align-items-center gap-2">
                      <button
                        type="button"
                        className="btn text-white fw-bold shadow-sm d-inline-flex align-items-center justify-content-center border-0 px-3 py-2"
                        style={{
                          backgroundColor: "#00bcd4",
                          borderRadius: "50px",
                          fontSize: "14px",
                        }}
                        onClick={handleDownloadCSV}
                      >
                        <i className="isax isax-add-circle me-1" /> Download CSV
                      </button>
                      <div style={{ width: "220px" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Cari user, order ID, kode..."
                          value={searchUsages}
                          onChange={(e) => {
                            setSearchUsages(e.target.value);
                            setPageUsages(1);
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-hover align-middle border-top">
                      <thead className="table-light">
                        <tr>
                          <th>Order Id kode</th>
                          <th>Date</th>
                          <th>Nama Produk</th>
                          <th>Kode Promo yang Dipakai</th>
                          <th>Harga sebelum apply</th>
                          <th>Harga setelah apply</th>
                          <th>User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingUsages ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4">
                              <div className="spinner-border text-primary spinner-border-sm me-2" role="status" />
                              Memuat data penggunaan...
                            </td>
                          </tr>
                        ) : usages.length === 0 ? (
                          <tr>
                            <td colSpan={7} className="text-center py-4 text-muted">
                              Belum ada riwayat penggunaan kode promo.
                            </td>
                          </tr>
                        ) : (
                          usages.map((u) => {
                            const priceBefore = Number(u.total || 0) + Number(u.discount_amount || 0);
                            return (
                              <tr key={u.id}>
                                <td>
                                  <span className="font-monospace fw-bold text-danger">{u.order_id}</span>
                                </td>
                                <td>
                                  <div className="small text-muted">
                                    {new Date(u.created_at).toLocaleDateString("id-ID", {
                                      year: "2-digit",
                                      month: "2-digit",
                                      day: "2-digit",
                                    })}
                                  </div>
                                </td>
                                <td>
                                  <div className="fw-medium text-truncate" style={{ maxWidth: "200px" }}>
                                    {u.course?.title || "-"}
                                  </div>
                                  {u.course?.type && (
                                    <span className="badge bg-info text-white fs-11">
                                      {u.course.type}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <span className="badge bg-danger px-2 py-1 fs-12">
                                    {u.promo_code_str || u.promo_code?.code || "-"}
                                  </span>
                                </td>
                                <td>
                                  <span className="fw-semibold text-secondary">
                                    {rupiahFormatter(priceBefore)}
                                  </span>
                                </td>
                                <td>
                                  <span className="fw-bold text-success">{rupiahFormatter(u.total)}</span>
                                  {u.discount_amount > 0 && (
                                    <div className="small text-muted">
                                      (Hemat {rupiahFormatter(u.discount_amount)})
                                    </div>
                                  )}
                                </td>
                                <td>
                                  <div className="fw-semibold small">{u.fullname || u.user?.name || "User"}</div>
                                  <div className="small text-muted">{u.email}</div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  {totalUsages > 0 && (
                    <div className="mt-3">
                      <CustomPagination
                        current={pageUsages}
                        total={totalUsages}
                        pageSize={limitUsages}
                        onChange={(p) => setPageUsages(p)}
                        onPageSizeChange={(sz) => {
                          setLimitUsages(sz);
                          setPageUsages(1);
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL BUAT / EDIT KODE PROMO */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content rounded-3 border-0 shadow">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title fw-bold text-white">
                  {editingCode ? "Edit Kode Promo" : "Buat Kode Promo Baru"}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <form onSubmit={handleSave}>
                <div className="modal-body p-4">
                  <div className="row g-3">
                    {/* Kode Promo */}
                    <div className="col-12">
                      <label className="form-label fw-semibold">
                        Kode Promo <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control text-uppercase fw-bold"
                        placeholder="Contoh: GUSTI1"
                        value={codeStr}
                        onChange={(e) => setCodeStr(e.target.value)}
                        required
                      />
                    </div>

                    {/* Diskon Live Teaching */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Diskon Live Teaching (LT)</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          style={{ maxWidth: "130px" }}
                          value={discLtType}
                          onChange={(e: any) => setDiscLtType(e.target.value)}
                        >
                          <option value="PERCENTAGE">Persen (%)</option>
                          <option value="NOMINAL">Nominal (Rp)</option>
                        </select>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Nilai diskon LT"
                          value={discLtVal}
                          onChange={(e) => setDiscLtVal(e.target.value)}
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Diskon Course */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Diskon E-Course</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          style={{ maxWidth: "130px" }}
                          value={discCourseType}
                          onChange={(e: any) => setDiscCourseType(e.target.value)}
                        >
                          <option value="PERCENTAGE">Persen (%)</option>
                          <option value="NOMINAL">Nominal (Rp)</option>
                        </select>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Nilai diskon Course"
                          value={discCourseVal}
                          onChange={(e) => setDiscCourseVal(e.target.value)}
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Diskon CBT */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Diskon CBT / Ukom</label>
                      <div className="input-group">
                        <select
                          className="form-select"
                          style={{ maxWidth: "130px" }}
                          value={discCbtType}
                          onChange={(e: any) => setDiscCbtType(e.target.value)}
                        >
                          <option value="PERCENTAGE">Persen (%)</option>
                          <option value="NOMINAL">Nominal (Rp)</option>
                        </select>
                        <input
                          type="number"
                          className="form-control"
                          placeholder="Nilai diskon CBT"
                          value={discCbtVal}
                          onChange={(e) => setDiscCbtVal(e.target.value)}
                          min="0"
                        />
                      </div>
                    </div>

                    {/* Max Uses */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Batas Penggunaan (Opsional)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Contoh: 100 (kosongkan jika tanpa batas)"
                        value={maxUses}
                        onChange={(e) => setMaxUses(e.target.value)}
                        min="1"
                      />
                    </div>

                    {/* Expire Date */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">Tanggal Kadaluarsa (Opsional)</label>
                      <input
                        type="datetime-local"
                        className="form-control"
                        value={expiresAt}
                        onChange={(e) => setExpiresAt(e.target.value)}
                      />
                    </div>

                    {/* Status Active */}
                    <div className="col-md-6 d-flex align-items-center pt-4">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="activeSwitch"
                          checked={isActive}
                          onChange={(e) => setIsActive(e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                        <label className="form-check-label fw-semibold ms-2" htmlFor="activeSwitch">
                          Status Aktif
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={saving}
                  >
                    Batal
                  </button>
                  <button type="submit" className="btn btn-primary fw-semibold" disabled={saving}>
                    {saving ? "Menyimpan..." : "Simpan Kode Promo"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminPromoCode;
